import { Injectable } from '@angular/core';
import { ErrorResponse } from '@app/core/models/error-response.model';
import * as RelationRelationActions from '@app/modules/relation-relation/actions/relation-relation-api.actions';
import * as RelationRelation from '@app/modules/relation-relation/reducers';
import { RelationRelationService } from '@app/modules/relation-relation/services/relation-relation.service';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { catchError, debounceTime, exhaustMap, map, of, switchMap, tap } from 'rxjs';

@Injectable()
export class RelationRelationEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly relationRelationService: RelationRelationService,
        private readonly store: Store<RelationRelation.State>,
        private readonly messageService: NzMessageService,
        private readonly translate: TranslateService,
    ) {}

    /**
     * Get effects
     */
    loadRelationActivities$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RelationRelationActions.GetOverview),
            concatLatestFrom(() => [
                this.store.select(RelationRelation.selectChosenRelationId),
                this.store.select(RelationRelation.selectRelationRelationOverviewFilter),
                this.store.select(RelationRelation.selectActivityOverviewPagination),
                this.store.select(RelationRelation.selectRelationRelationOverviewSorting),
            ]),
            exhaustMap(([, relationId, filters, pagination, sorting]) =>
                this.relationRelationService.getOverview(relationId, sorting, pagination, filters).pipe(
                    map((response) => RelationRelationActions.GetOverviewSuccess(response)),
                    catchError((response: { error: ErrorResponse }) =>
                        of(RelationRelationActions.GetOverviewError({ response: response.error })),
                    ),
                ),
            ),
        ),
    );

    loadRelationActivitiesFilterChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RelationRelationActions.FilterOverview),
            map(() => RelationRelationActions.GetOverview()),
        ),
    );

    /**
     * create  effects
     */

    createActivityRelation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RelationRelationActions.Create),
            exhaustMap((action) =>
                this.relationRelationService.create(action).pipe(
                    map((activity) => RelationRelationActions.CreateSuccess(activity)),
                    catchError((response: { error: ErrorResponse }) =>
                        of(RelationRelationActions.CreateError({ response: response.error })),
                    ),
                ),
            ),
        ),
    );

    createActivityRelationSuccessToast$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RelationRelationActions.CreateSuccess),
            tap(() =>
                this.translate
                    .get('activity-relation.notification.add')
                    .subscribe((translatedMessage: string) => this.messageService.success(translatedMessage)),
            ),
            switchMap(() => of(RelationRelationActions.GetOverview(), RelationRelationActions.ResetCreate())),
        ),
    );

    createActivityRelationErrorToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RelationRelationActions.CreateError),
                tap(() =>
                    this.translate
                        .get('relation.notification.error')
                        .subscribe((translatedMessage: string) => this.messageService.error(translatedMessage)),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Get acitvityRelation effects
     */

    getRelations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RelationRelationActions.GetRelations),
            debounceTime(350),
            exhaustMap((action) =>
                this.relationRelationService.getRelations(action.searchQuery, action.skipCount).pipe(
                    map((relations) => RelationRelationActions.GetRelationsSuccess({ relations })),
                    catchError((response: { error: ErrorResponse }) =>
                        of(RelationRelationActions.GetRelationsError({ response: response.error })),
                    ),
                ),
            ),
        ),
    );

    /**
     * Delete effects
     */
    deleteRelationRelation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RelationRelationActions.Delete),
            exhaustMap((action) =>
                this.relationRelationService.delete(Number(action.id)).pipe(
                    map(() => RelationRelationActions.DeleteSuccess()),
                    catchError((response: { error: ErrorResponse }) =>
                        of(RelationRelationActions.DeleteError({ response: response.error })),
                    ),
                ),
            ),
        ),
    );

    deleteRelationRelationSuccessReload$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RelationRelationActions.DeleteSuccess),
            map(() => RelationRelationActions.GetOverview()),
        ),
    );

    deleteRelationRelationSuccessToast$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(RelationRelationActions.DeleteSuccess),
                tap(() =>
                    this.translate
                        .get('note.modal.notification.delete')
                        .subscribe((translatedMessage: string) => this.messageService.success(translatedMessage)),
                ),
            ),
        { dispatch: false },
    );
}
